import { LoadingScreen } from '@avanta/shared/components/LoadingScreen';
import { PaginatedTable } from '@avanta/shared/components/PaginatedTable';
import { Button } from '@avanta/shared/components/ui/Button';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { useDialogContext } from '@avanta/shared/contexts/Dialog';
import { useDelayedLoading } from '@avanta/shared/hooks/useDelayedLoading';
import { t } from '@avanta/shared/i18n';
import { type TableFilterOption } from '@avanta/shared/types/table';
import { type User } from '@avanta/shared/types/users';
import { getStatusBadge } from '@avanta/shared/utils/appointments';
import { type ColumnDef } from '@tanstack/react-table';
import { Plus, Upload } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PageHeader } from 'components/PageHeader';
import { type AppointmentWithDetails } from 'types/appointments';
import { AddPatientDialog } from './components/AddPatientDialog';
import { ImportPatientsDialog } from './components/ImportPatientsDialog';
import { TableActions } from './components/TableActions';
import { useFetchInsuredUsers } from './hooks/useFetchInsuredUsers';
import { usePatientsAppointmentSummary } from './hooks/usePatientsAppointmentSummary';
import { getPatientName, PATIENT_SESSION_FILTER, type PatientSessionFilter } from './utils';

const EMPTY_CELL = '-';

const getSessionFilterOptions = (): TableFilterOption<PatientSessionFilter>[] => [
  {
    key: PATIENT_SESSION_FILTER.WITH_SESSION,
    label: t('InsuredPersonsList.filters.withSession', { ns: LOCIZE_NS.INSURANCE }),
  },
  {
    key: PATIENT_SESSION_FILTER.WITHOUT_SESSION,
    label: t('InsuredPersonsList.filters.withoutSession', { ns: LOCIZE_NS.INSURANCE }),
  },
];

const getColumns = (
  appointmentsByPatientId: Map<string, AppointmentWithDetails>
): ColumnDef<User>[] => [
  {
    id: 'name',
    header: t('InsuredPersonsList.Table.name.label', { ns: LOCIZE_NS.INSURANCE }),
    accessorFn: row => getPatientName(row),
    enableSorting: true,
    cell: ({ getValue }): string => String(getValue() ?? ''),
  },
  {
    id: 'nif',
    accessorKey: 'nif',
    header: t('InsuredPersonsList.Table.nif.label', { ns: LOCIZE_NS.INSURANCE }),
  },
  {
    id: 'fileNumber',
    header: t('InsuredPersonsList.Table.fileNumber.label', { ns: LOCIZE_NS.INSURANCE }),
    cell: (): string => EMPTY_CELL,
  },
  {
    id: 'email',
    accessorKey: 'email',
    header: t('InsuredPersonsList.Table.email.label', { ns: LOCIZE_NS.INSURANCE }),
    cell: ({ row }): React.ReactElement => (
      <span className="block max-w-48 truncate" title={row.original.email}>
        {row.original.email}
      </span>
    ),
  },
  {
    id: 'sessionStatus',
    header: t('InsuredPersonsList.Table.sessionStatus.label', { ns: LOCIZE_NS.INSURANCE }),
    enableSorting: true,
    accessorFn: row => appointmentsByPatientId.has(row.id),
    cell: ({ row }): React.ReactElement => {
      const appointment = appointmentsByPatientId.get(row.original.id);

      return appointment ? (
        getStatusBadge(appointment)
      ) : (
        <span className="text-gray-400">{EMPTY_CELL}</span>
      );
    },
  },
  {
    id: 'actions',
    header: t('InsuredPersonsList.Table.actions.label', { ns: LOCIZE_NS.INSURANCE }),
    cell: ({ row }): React.ReactElement => (
      <TableActions
        patientId={row.original.id}
        hasUpcomingSession={appointmentsByPatientId.has(row.original.id)}
      />
    ),
  },
];

export const Patients = (): React.ReactElement => {
  const { t } = useTranslation(LOCIZE_NS.INSURANCE);
  const {
    appointmentsByPatientId,
    withSession,
    isLoading: isLoadingAppointments,
  } = usePatientsAppointmentSummary();
  const columns = useMemo(() => getColumns(appointmentsByPatientId), [appointmentsByPatientId]);
  const { table, query, employeeTotalCount, isLoading, selectedStatusFilter, setStatusFilter } =
    useFetchInsuredUsers(columns, appointmentsByPatientId);
  const { showDialog } = useDialogContext();
  const showLoading = useDelayedLoading(query.isFetching);

  if (isLoading || isLoadingAppointments) {
    return <LoadingScreen />;
  }

  const stats = {
    total: employeeTotalCount,
    withSession,
    withoutSession: Math.max(employeeTotalCount - withSession, 0),
  };

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={t('InsuredPersonsList.heading')}
        description={t('InsuredPersonsList.description', stats)}
      >
        <Button
          type="button"
          variant="primary"
          size="md"
          icon="leading"
          onClick={() => showDialog({ component: <AddPatientDialog /> })}
        >
          <Plus />
          {t('InsuredPersonsList.addPerson.cta')}
        </Button>
        <Button
          type="button"
          variant="terciary"
          size="md"
          icon="leading"
          onClick={() => showDialog({ component: <ImportPatientsDialog /> })}
        >
          <Upload />
          {t('InsuredPersonsList.addPersonByCSV.cta')}
        </Button>
      </PageHeader>
      <PaginatedTable<User, PatientSessionFilter>
        table={table}
        columns={columns}
        isLoading={showLoading}
        emptyTitle={t('InsuredPersonsList.empty')}
        tableClassName="w-full"
        withSearch
        searchPlaceholder="InsuredPersonsList.search.placeholder"
        searchPlaceholderNs={LOCIZE_NS.INSURANCE}
        filter={{
          accessorKey: 'id',
          allLabel: t('InsuredPersonsList.filters.all'),
          options: getSessionFilterOptions(),
        }}
        selectedFilterKey={selectedStatusFilter}
        setFilter={setStatusFilter}
      />
    </div>
  );
};
